import {
	ConversationImportResult,
	conversationImportResultSchema,
	ImportedConversationHistory,
	importedConversationHistorySchema,
} from "@signalist/shared-types";

import { api } from "@/redux/api";

import { ProspectInterest } from "@/enum/prospect.enum";
import { CalendarInviteResponseType } from "@/enum/conversation.enum";
import { CcRecipientInfo, CcRecipientInfoListSchema } from "@/schemas/cc-recipient";

import { ConversationSchema, Conversation } from "../../pages/Apollo/schema/get-people";

export const conversationApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// Pass emails lowercased + sorted so equal CC lists share one cache entry.
		resolveCcRecipients: builder.query<CcRecipientInfo[], string[]>({
			query: (emails) => ({
				url: "conversation/resolve-cc-recipients",
				method: "POST",
				body: { emails },
				responseSchema: CcRecipientInfoListSchema,
			}),
		}),
		updateConversationSentiment: builder.mutation<
			Partial<Conversation>,
			{
				id: number;
				sentiment: ProspectInterest;
			}
		>({
			query: ({ id, ...body }) => ({
				url: `conversation/${id}`,
				method: "PATCH",
				body,
				responseSchema: ConversationSchema,
			}),
		}),
		// PREC-4305: the viewer's imported earlier conversation with the lead, per list person.
		getImportedConversations: builder.query<ImportedConversationHistory, number>({
			query: (listPersonId) => ({
				url: `conversation-import/list-person/${listPersonId}`,
				responseSchema: importedConversationHistorySchema,
			}),
			providesTags: (_result, _error, listPersonId) => [
				{ type: "ImportedConversation", id: listPersonId },
			],
		}),
		importEarlierConversation: builder.mutation<ConversationImportResult, number>({
			query: (listPersonId) => ({
				url: `conversation-import/list-person/${listPersonId}`,
				method: "POST",
				responseSchema: conversationImportResultSchema,
			}),
			// Refetched even after a failed source: what the other source imported is shown.
			invalidatesTags: (_result, _error, listPersonId) => [
				{ type: "ImportedConversation", id: listPersonId },
			],
		}),
		respondToCalendarInvite: builder.mutation<
			Partial<Conversation>,
			{
				id: number;
				response: CalendarInviteResponseType;
			}
		>({
			query: ({ id, response }) => ({
				url: `schedule/conversations/${id}/calendar-response`,
				method: "POST",
				body: { response },
				responseSchema: ConversationSchema,
			}),
		}),
	}),
});

export const {
	useGetImportedConversationsQuery,
	useImportEarlierConversationMutation,
	useUpdateConversationSentimentMutation,
	useRespondToCalendarInviteMutation,
	useResolveCcRecipientsQuery,
} = conversationApi;
export const conversationApiReducer = conversationApi.reducer;
export const conversationApiMiddleware = conversationApi.middleware;
